import { Heading } from 'components/ui/Heading/Heading';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './Coupon.scss';
import {
  createPayment,
  setDisplayCoupon,
  trackCreateCheckoutPayment,
} from 'actions';
import { addCoupon } from 'actions/entities/coupons/addCoupon/addCoupon';
import { Button } from 'components/ui/Button/Button';
import { Spinner } from 'components/ui/Spinner';
import { TextInput } from 'components/ui/TextInput/TextInput';
import {
  getOrder,
  getOrderCouponDiscount,
  getOrderOriginalAmount,
  getOrderTotalAmount,
} from 'selectors';
import { useTranslations } from 'utils';
import { CouponSummary } from './CouponSummary/CouponSummary';

export function Coupon() {
  const dispatch = useDispatch();
  const i18n = useTranslations();

  const order = useSelector(getOrder);
  const orderAmount = useSelector(getOrderOriginalAmount);
  const orderCouponDiscount = useSelector(getOrderCouponDiscount);
  const orderTotalAmount = useSelector(getOrderTotalAmount);

  const { id, token } = order;
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const [isFetchingOrder, setIsFetchingOrder] = useState(false);
  const [isAddCouponSuccess, setIsAddCouponSuccess] = useState(false);

  const handleApplyCoupon = async () => {
    try {
      await handleAddCouponToOrder();
    } catch (error) {
      handleError(error);
    }
  };

  const handleError = error => {
    if (error?.status === 422) {
      showCouponError(i18n.t('coupon.serverError.invalid'));
    } else {
      showCouponError(i18n.t('coupon.serverError.unable'));
    }
  };
  const showCouponError = message => {
    setIsFetchingOrder(false);
    setCouponError(message);
  };

  const handleCouponCode = (_name: string, value: string) => {
    setCouponCode(value);
  };

  const handleSkip = async () => {
    await handleCreatePayment();
  };

  const handleNext = async () => {
    if (isAddCouponSuccess) {
      await handleCreatePayment();
    }
  };

  async function handleCreatePayment() {
    try {
      setIsFetchingOrder(true);
      await dispatch(createPayment());
      dispatch(trackCreateCheckoutPayment());
      setIsFetchingOrder(false);
      dispatch(setDisplayCoupon(false));
    } catch (_error) {
      dispatch(setDisplayCoupon(false));
    }
  }

  async function handleAddCouponToOrder() {
    setIsFetchingOrder(true);
    await dispatch(addCoupon({ id, token, couponCode }));
    setIsFetchingOrder(false);
    setIsAddCouponSuccess(true);
  }

  if (isFetchingOrder) {
    return <Spinner />;
  }

  return (
    <div className="EnterCoupon flex flex-col h-full">
      <div className="flex-1">
        <div className="EnterCoupon-container">
          <Heading as="h2" size="large" className="fontWeight-semiBold">
            {i18n.t('coupon.enterCoupon.title')}
          </Heading>
          <div className="EnterCoupon-description">
            {i18n.t('coupon.enterCoupon.description')}
          </div>
          <div className="EnterCoupon-content">
            {!isAddCouponSuccess && !isFetchingOrder && (
              <div
                className="EnterCoupon-inputField"
                data-testid="enterCouponContainer"
              >
                <TextInput
                  error={couponError}
                  data-testid="enterCouponInput"
                  floatingLabel
                  label={i18n.t('coupon.enterCoupon.apply.input') as string}
                  name="enterCoupon-input"
                  type="text"
                  value={couponCode}
                  onChange={handleCouponCode}
                />
                <Button
                  tone="secondary"
                  size="full"
                  data-testid="enterCouponApplyButton"
                  onClick={handleApplyCoupon}
                >
                  {i18n.t('coupon.enterCoupon.apply.button')}
                </Button>
              </div>
            )}

            {isAddCouponSuccess && (
              <CouponSummary
                originalAmount={orderAmount}
                discountAmount={orderCouponDiscount}
                totalAmount={orderTotalAmount}
              />
            )}
          </div>
        </div>
      </div>
      <div className="pt-6 pb-2">
        <Button
          disabled={isFetchingOrder}
          onClick={handleNext}
          tone="primary"
          size="full"
          data-testid="enterCouponNextButton"
        >
          {i18n.t('checkout.navigation.next')}
        </Button>
        {!isAddCouponSuccess && !isFetchingOrder && (
          <button
            disabled={isFetchingOrder}
            className="EnterCoupon-noCoupon"
            onClick={handleSkip}
            data-testid="enterCouponSkipButton"
          >
            {i18n.t('coupon.enterCoupon.skip.text')}
          </button>
        )}
      </div>
    </div>
  );
}
